import { useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { ClinicalProtocolDTOSchema } from "@arna-med/shared";
import { useBackendAuthenticated } from "@/hooks/useBackendAuthenticated";

const ProtocolsResponseSchema = z.object({
  success: z.boolean(),
  data: z.array(ClinicalProtocolDTOSchema),
});

export type ClinicalProtocol = z.infer<typeof ClinicalProtocolDTOSchema>;

/**
 * Diagnosis search over the clinical-protocol base.
 *
 * Idle below two characters — the endpoint returns nothing for shorter queries,
 * and a request per keystroke of a one-letter query is pure noise.
 */
export function useProtocolSearch(query: string, language: string) {
  const backend = useBackendAuthenticated();
  const term = query.trim();
  return useQuery({
    queryKey: ["clinical-protocols", term, language],
    queryFn: async () => {
      const json = await backend({
        method: "GET",
        url: `/api/v1/clinical-protocols?q=${encodeURIComponent(term)}&language=${language}`,
      });
      return ProtocolsResponseSchema.parse(json).data;
    },
    enabled: term.length >= 2,
    staleTime: 60_000,
  });
}
